/**
 * ルームごとに共有する、ブロック用プロシージャルテクスチャのレシピ。
 * 画像そのものではなくこの値だけを同期し、各クライアントで同じ画像を生成する。
 */

export const BLOCK_TEXTURE_PATTERNS = [
  'plain',
  'outline',
  'double-outline',
  'inset',
  'horizontal-stripes',
  'vertical-stripes',
  'diagonal-stripes',
  'checker',
  'grid',
  'bricks',
  'planks',
  'dots',
] as const;

export type BlockTexturePattern = (typeof BLOCK_TEXTURE_PATTERNS)[number];

export const BLOCK_TEXTURE_PATTERN_LABELS: Record<BlockTexturePattern, string> = {
  plain: '無地',
  outline: '1px外周',
  'double-outline': '二重外周',
  inset: '凹パネル',
  'horizontal-stripes': '横ストライプ',
  'vertical-stripes': '縦ストライプ',
  'diagonal-stripes': '斜めストライプ',
  checker: '市松模様',
  grid: 'グリッド',
  bricks: 'レンガ積み',
  planks: '板張り',
  dots: 'ドット',
};

export const BLOCK_TEXTURE_RESOLUTIONS = [16, 32, 64] as const;
export type BlockTextureResolution = (typeof BLOCK_TEXTURE_RESOLUTIONS)[number];

/** list フィールドが無い旧テクスチャのフォールバック表示名。 */
export const UNCATEGORIZED_TEXTURE_LIST = '未分類';

export const MAX_TEXTURE_LIST_LENGTH = 32;

/** 作成者ごとの初期リスト名。 */
export function defaultTextureList(authorName: string): string {
  const name = authorName.replace(/[\u0000-\u001f\u007f]/g, '').trim() || 'anonymous';
  return `デフォルト（${name}）`;
}

export interface BlockTextureDefinition {
  /** Entity.material にそのまま入る、ルーム内で一意な ID。 */
  id: string;
  name: string;
  /** インベントリ上の所属リスト（カテゴリ）名。 */
  list: string;
  /** 0xRRGGBB。明暗色はクライアントがこの1色から自動生成する。 */
  color: number;
  pattern: BlockTexturePattern;
  resolution: BlockTextureResolution;
  /** ノイズの濃淡が変化する空間スケール（テクスチャ上のpixel数）。 */
  noiseSize: number;
  /** 0〜1。0ならノイズなし。 */
  noiseContrast: number;
  /** 全クライアントで同じ模様を再現するためのseed。 */
  seed: number;
  createdAt?: number;
}

export type BlockTextureValidationResult =
  | { ok: true; value: BlockTextureDefinition }
  | { ok: false; error: string };

const TEXTURE_ID_PATTERN = /^texture:[A-Za-z0-9_-]{1,56}$/;
const MAX_TEXTURE_NAME_LENGTH = 32;
const MAX_SEED = 0x7fffffff;

function sanitizeTextureLabel(value: string): string {
  return value.replace(/[\u0000-\u001f\u007f]/g, '').trim();
}

function fail(error: string): BlockTextureValidationResult {
  return { ok: false, error };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** WebSocket越しのレシピを検証し、既知フィールドだけへ正規化する。 */
export function validateBlockTexture(input: unknown): BlockTextureValidationResult {
  if (!isRecord(input)) return fail('texture must be an object');

  if (typeof input.id !== 'string' || !TEXTURE_ID_PATTERN.test(input.id)) {
    return fail('texture.id must start with texture: and contain only safe characters');
  }

  if (typeof input.name !== 'string') return fail('texture.name must be a string');
  const name = sanitizeTextureLabel(input.name);
  if (name.length === 0 || name.length > MAX_TEXTURE_NAME_LENGTH) {
    return fail(`texture.name must be 1-${MAX_TEXTURE_NAME_LENGTH} characters`);
  }

  let list: string;
  if (input.list === undefined || input.list === null) {
    list = UNCATEGORIZED_TEXTURE_LIST;
  } else if (typeof input.list !== 'string') {
    return fail('texture.list must be a string');
  } else {
    list = sanitizeTextureLabel(input.list);
    if (list.length === 0 || list.length > MAX_TEXTURE_LIST_LENGTH) {
      return fail(`texture.list must be 1-${MAX_TEXTURE_LIST_LENGTH} characters`);
    }
  }

  if (
    typeof input.color !== 'number' ||
    !Number.isInteger(input.color) ||
    input.color < 0 ||
    input.color > 0xffffff
  ) {
    return fail('texture.color must be an integer between 0x000000 and 0xffffff');
  }

  if (
    typeof input.pattern !== 'string' ||
    !BLOCK_TEXTURE_PATTERNS.includes(input.pattern as BlockTexturePattern)
  ) {
    return fail(`texture.pattern must be one of ${BLOCK_TEXTURE_PATTERNS.join(' | ')}`);
  }

  if (
    typeof input.resolution !== 'number' ||
    !BLOCK_TEXTURE_RESOLUTIONS.includes(input.resolution as BlockTextureResolution)
  ) {
    return fail(`texture.resolution must be one of ${BLOCK_TEXTURE_RESOLUTIONS.join(' | ')}`);
  }

  if (
    typeof input.noiseSize !== 'number' ||
    !Number.isInteger(input.noiseSize) ||
    input.noiseSize < 1 ||
    input.noiseSize > 16
  ) {
    return fail('texture.noiseSize must be an integer between 1 and 16');
  }

  if (
    typeof input.noiseContrast !== 'number' ||
    !Number.isFinite(input.noiseContrast) ||
    input.noiseContrast < 0 ||
    input.noiseContrast > 1
  ) {
    return fail('texture.noiseContrast must be between 0 and 1');
  }

  if (
    typeof input.seed !== 'number' ||
    !Number.isInteger(input.seed) ||
    input.seed < 0 ||
    input.seed > MAX_SEED
  ) {
    return fail(`texture.seed must be an integer between 0 and ${MAX_SEED}`);
  }

  return {
    ok: true,
    value: {
      id: input.id,
      name,
      list,
      color: input.color,
      pattern: input.pattern as BlockTexturePattern,
      resolution: input.resolution as BlockTextureResolution,
      noiseSize: input.noiseSize,
      noiseContrast: input.noiseContrast,
      seed: input.seed,
    },
  };
}
